import React, { useEffect, useState } from "react";
import axios from "axios";
import QRCode from "qrcode";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { ScrollText, Loader2, XCircle, BadgeCheck, PenLine, FileText, ShieldCheck, Undo2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtUSD = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" ");
  return d || e.message || "Something went wrong.";
};

export default function SecuritiesLoanSign() {
  const { loanId } = useParams();
  const [params] = useSearchParams();
  const t = params.get("t");
  const [loan, setLoan] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ signer_name: "", signer_title: "", accepted: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [qr, setQr] = useState("");
  const [ack, setAck] = useState({ name: "", busy: false, done: false });
  const [feeMsg, setFeeMsg] = useState("");

  const base = process.env.REACT_APP_BACKEND_URL;
  const load = async () => {
    try {
      const r = await axios.get(`${API}/securities/loans/${loanId}/public?t=${t}`);
      setLoan(r.data.loan);
      if (r.data.loan?.verify_token) {
        QRCode.toDataURL(`${base}/securities/verify/${r.data.loan.verify_token}`, { margin: 1, width: 220, errorCorrectionLevel: "H" }).then(setQr).catch(() => {});
      }
    } catch (e) { setError(errMsg(e)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  // Finalize counterparty fee payment on return from Stripe
  useEffect(() => {
    const fs = params.get("fee_session");
    if (fs) {
      axios.get(`${API}/securities/loans/${loanId}/fee-status?session_id=${fs}&t=${t}`)
        .then((r) => { if (r.data.paid) { setFeeMsg("✓ Lending fee paid — funds are being remitted to the lender."); load(); } })
        .catch(() => {});
    }
  }, []); // eslint-disable-line

  const payFee = async () => {
    try {
      const { data } = await axios.post(`${API}/securities/loans/${loanId}/pay-counterparty-fee?t=${t}`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { setError(errMsg(e)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const r = await axios.post(`${API}/securities/loans/${loanId}/countersign?t=${t}`, { signer_name: form.signer_name, signer_title: form.signer_title || null });
      setDone(r.data.fully_executed);
      await load();
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  const acknowledgeReturn = async (e) => {
    e.preventDefault();
    setAck((a) => ({ ...a, busy: true }));
    try {
      await axios.post(`${API}/securities/loans/${loanId}/acknowledge-return?t=${t}`, { signer_name: ack.name });
      setAck((a) => ({ ...a, done: true, busy: false }));
      await load();
    } catch (err) { setError(errMsg(err)); setAck((a) => ({ ...a, busy: false })); }
  };

  const ag = loan?.agreement;
  const borrowerSigned = (loan?.signatures || []).some((s) => (s.signer_role || "").toLowerCase() === "borrower");

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#ECECE4] px-4 py-10" data-testid="securities-loan-sign-page">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="text-[12px] text-[#8A8A8A] hover:text-[#ECECE4]">← Return to Home</Link>
        <div className="mt-5 bg-[#13130E] border border-[#2A2A22] rounded-xl overflow-hidden">
          <div className="px-7 py-6 border-b border-[#2A2A22] flex items-center gap-3">
            <ScrollText size={22} className="text-[#C9A227]" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8A8A]">SEUTRADE FINANCIAL SERVICES · Securities Lending</div>
              <div className="text-xl font-serif">Securities Loan Agreement</div>
            </div>
          </div>

          {error && !loan ? (
            <div className="px-7 py-12 text-center" data-testid="sls-error"><XCircle size={34} className="text-[#FF8585] mx-auto mb-3" /><div className="text-[#FF8585] text-sm">{error}</div></div>
          ) : !loan ? (
            <div className="px-7 py-16 text-center"><Loader2 className="animate-spin inline text-[#C9A227]" /></div>
          ) : (
            <div className="px-7 py-6" data-testid="sls-body">
              <p className="text-[13px] text-[#9A9A90] mb-5">{ag?.subtitle}</p>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-[13px] mb-6">
                <Info label="Loan No." value={loan.loan_no} />
                <Info label="Instrument" value={loan.type_label} />
                <Info label="Lender" value={ag?.lender?.name || ag?.lender?.entity} />
                <Info label="Borrower (you)" value={loan.counterparty_name} />
                <Info label="Loaned Amount" value={fmtUSD(loan.amount)} highlight />
                <Info label="Term" value={`${loan.term_days} days`} />
                <Info label="Maturity" value={loan.maturity_date} />
                <Info label={`Loan Fee (${loan.counterparty_fee_rate}% p.a.)`} value={fmtUSD(loan.counterparty_fee)} highlight />
              </div>

              <div className="rounded-md bg-[#1A1606] border border-[#B8860B]/30 p-4 mb-6 flex items-center gap-3" data-testid="sls-instrument">
                <ScrollText size={20} className="text-[#C9A227]" />
                <div className="text-[13px]">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A]">Instrument being lent to you</div>
                  <div className="text-[#ECECE4]"><strong className="text-[#E4C97A]">{loan.type_label}</strong> · {fmtUSD(loan.amount)} USD · Account {loan.account_no}{loan.beneficiary_name ? ` · Beneficiary ${loan.beneficiary_name}` : ""}</div>
                </div>
              </div>

              <div className="rounded-lg border border-[#2A2A22] bg-[#0F0F0A] p-5 mb-6 max-h-72 overflow-y-auto" data-testid="sls-clauses">
                {(ag?.clauses || []).map((c, i) => <p key={i} className="text-[12px] text-[#C9C9C0] leading-relaxed mb-3">{c}</p>)}
              </div>

              <div className="flex flex-wrap items-center gap-5 mb-6">
                <a href={`${API}/securities/loans/${loanId}/agreement.pdf?t=${t}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-[#9DB4E8] hover:text-[#c3d3f5]"><FileText size={13} /> Download agreement PDF</a>
                {qr && (
                  <div className="flex items-center gap-3" data-testid="sls-verify-qr">
                    <img src={qr} alt="verify qr" className="w-20 h-20 rounded bg-white p-1.5" />
                    <div className="text-[11px] text-[#9A9A90] max-w-[240px]">
                      <div className="inline-flex items-center gap-1 text-[#E4C97A]"><ShieldCheck size={13} /> Scan to verify authenticity</div>
                      <div className="mt-1 break-all font-mono text-[9px] text-[#6C6C6C]">SHA-256 {loan.verify_hash}</div>
                    </div>
                  </div>
                )}
              </div>

              {loan.notary && (
                <div className="rounded-lg border border-[#B8860B]/40 bg-[#1A1606] p-4 mb-6" data-testid="sls-notary">
                  <div className="inline-flex items-center gap-1.5 text-[#E4C97A] text-[12px] font-bold"><BadgeCheck size={14} /> Notary & Transfer Agent Seal</div>
                  <div className="text-[12px] text-[#ECECE4] mt-1">{loan.notary.name} · {loan.notary.title}</div>
                  <div className="text-[11px] text-[#9A9A90] mt-1">Commission {loan.notary.commission_no} · Notarized {(loan.notary.notarized_at || "").slice(0, 19)} UTC</div>
                  <div className="text-[9px] font-mono text-[#6C6C6C] mt-1 break-all">Seal {loan.notary.seal_hash}</div>
                </div>
              )}

              {loan.status === "recall_requested" && !ack.done ? (
                <form onSubmit={acknowledgeReturn} className="rounded-lg border border-[#B8860B]/40 bg-[#1A1606] p-5 space-y-3" data-testid="sls-return-form">
                  <div className="inline-flex items-center gap-1.5 text-[#E4C97A] text-[13px] font-bold"><Undo2 size={15} /> Recall requested — acknowledge return of collateral</div>
                  <input value={ack.name} onChange={(e) => setAck((a) => ({ ...a, name: e.target.value }))} placeholder="Your full legal name" data-testid="sls-return-name"
                    className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2.5 text-[14px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
                  <button type="submit" disabled={ack.busy || !ack.name} data-testid="sls-return-submit" className="inline-flex items-center gap-2 bg-[#C9A227] hover:bg-[#b8901f] disabled:opacity-50 text-[#0A0A0A] px-6 py-2.5 rounded-md text-[12px] uppercase tracking-[0.14em] font-bold">{ack.busy ? <Loader2 size={14} className="animate-spin" /> : <Undo2 size={14} />} Acknowledge return</button>
                </form>
              ) : loan.status === "returned" || ack.done ? (
                <div className="rounded-lg border border-[#3a3a30] bg-[#13130E] p-5 text-center" data-testid="sls-returned"><Undo2 size={26} className="text-[#9A9A90] mx-auto mb-2" /><div className="text-[#9A9A90] font-serif">Collateral return acknowledged. This loan is closed.</div></div>
              ) : borrowerSigned ? (
                <div className="rounded-lg border border-[#0B6B4F]/50 bg-[#0B6B4F]/10 p-5 text-center" data-testid="sls-signed">
                  <BadgeCheck size={30} className="text-[#3DBB8E] mx-auto mb-2" />
                  <div className="text-[#3DBB8E] font-serif text-lg">You have executed this agreement.</div>
                  {done && <div className="text-[12px] text-[#9A9A90] mt-1">The loan is now fully executed by both parties.</div>}
                  <div className="mt-4">
                    {loan.counterparty_fee_paid ? (
                      <div className="text-[12px] text-[#3DBB8E]" data-testid="sls-fee-paid">✓ Lending fee of {fmtUSD(loan.counterparty_fee)} paid.</div>
                    ) : (
                      <>
                        <div className="text-[12px] text-[#E4C97A] mb-2">Lending fee due to the lender: <strong>{fmtUSD(loan.counterparty_fee)}</strong> ({loan.counterparty_fee_rate}% p.a.)</div>
                        <button onClick={payFee} data-testid="sls-pay-fee" className="inline-flex items-center gap-2 bg-[#C9A227] hover:bg-[#b8901f] text-[#0A0A0A] px-6 py-2.5 rounded-md text-[12px] uppercase tracking-[0.14em] font-bold">Pay lending fee</button>
                      </>
                    )}
                    {feeMsg && <div className="text-[12px] text-[#3DBB8E] mt-2" data-testid="sls-fee-msg">{feeMsg}</div>}
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="sls-sign-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A]">Your full legal name <span className="text-[#C9A227]">*</span></span>
                      <input value={form.signer_name} onChange={(e) => setForm((f) => ({ ...f, signer_name: e.target.value }))} required data-testid="sls-signer-name"
                        className="mt-1 w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2.5 text-[14px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
                    </label>
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A]">Title</span>
                      <input value={form.signer_title} onChange={(e) => setForm((f) => ({ ...f, signer_title: e.target.value }))} data-testid="sls-signer-title"
                        className="mt-1 w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2.5 text-[14px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
                    </label>
                  </div>
                  <label className="flex items-start gap-2 text-[12px] text-[#C9C9C0]">
                    <input type="checkbox" checked={form.accepted} onChange={(e) => setForm((f) => ({ ...f, accepted: e.target.checked }))} data-testid="sls-accept" className="mt-0.5" />
                    <span>I have read and agree to the Securities Loan Agreement above and consent to electronic signature under the E-SIGN Act & UETA.</span>
                  </label>
                  {error && <div className="text-[#FF8585] text-[12px]" data-testid="sls-sign-error">{error}</div>}
                  <button type="submit" disabled={busy || !form.accepted || !form.signer_name} data-testid="sls-sign-submit"
                    className="inline-flex items-center gap-2 bg-[#0B6B4F] hover:bg-[#0d7d5c] disabled:opacity-50 text-white px-7 py-3 rounded-md text-[12px] uppercase tracking-[0.16em] font-bold">
                    {busy ? <Loader2 size={15} className="animate-spin" /> : <PenLine size={15} />} E-sign the agreement
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Info = ({ label, value, highlight }) => (
  <div className="flex justify-between border-b border-[#1C1C16] py-1.5">
    <span className="text-[#8A8A8A]">{label}</span>
    <span className={highlight ? "text-[#3DBB8E] font-semibold" : "text-[#ECECE4]"}>{value}</span>
  </div>
);
